import type { SxProps, Theme } from '@mui/material';

// ─── Screen-reader only (visually hidden) ─────────────────────────────────────

export const srOnlySx: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0
};

// ─── Dialog paper ─────────────────────────────────────────────────────────────

export const dialogPaperSx: SxProps<Theme> = {
  m: 0,
  width: '100vw',
  maxWidth: '100vw',
  height: '100dvh',
  maxHeight: '100dvh',
  borderRadius: 0,
  backgroundColor: '#FFFFFF'
};

// ─── Outer flex wrapper (fills the dialog) ────────────────────────────────────

export const outerWrapSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%'
};

// ─── Scrollable content region ────────────────────────────────────────────────

export const scrollableContentSx: SxProps<Theme> = {
  flex: 1,
  overflowY: 'auto',
  overflowX: 'clip',
  scrollBehavior: 'smooth',
  WebkitOverflowScrolling: 'touch',
  px: { xs: 2, sm: 3, md: 4 },
  py: { xs: 3, sm: 4 },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
};

// ─── Inner content box (max-width 1200px, centred) ────────────────────────────

export const innerContentSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: '1200px',
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center'
};

// ─── Success icon ─────────────────────────────────────────────────────────────

export const successIconSx: SxProps<Theme> = {
  fontSize: { xs: 64, md: 80 },
  color: '#2E7D32',
  mb: { xs: 2, md: 2.5 },
  mt: { xs: 1, md: 2 }
};

// ─── Title ────────────────────────────────────────────────────────────────────

export const titleSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Display, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '20px', md: '28px' },
  lineHeight: { xs: '28px' },
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-primary, #000000)',
  mb: 1.5
};

// ─── Subtitle ─────────────────────────────────────────────────────────────────

export const subtitleSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 400,
  fontSize: { xs: '14px', sm: '16px', md: '18px' },
  lineHeight: { xs: '20px', sm: '24px' },
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-primary, #000000)',
  mb: { xs: 3, md: 4 }
};

// ─── Divider ──────────────────────────────────────────────────────────────────

export const dividerSx: SxProps<Theme> = {
  width: { xs: '100%', sm: '30%' },
  background: 'var(--Sem-brdr-colour-border-divider, #C9CACC)',
  mb: { xs: 3, md: 4 }
};

// ─── Section title ────────────────────────────────────────────────────────────

export const sectionTitleSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 700,
  fontSize: { md: '20px' },
  lineHeight: '28px',
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-primary, #000000)',
  mb: { xs: 3, md: 4 }
};

// ─── Card offset constants – shared with the component for translateX math ────

export const CARD_WIDTH_PX = 280;
export const CARD_GAP_PX = 24; // matches cardsContainerSx gap – used for translateX offset

// ─── Cards carousel track ─────────────────────────────────────────────────────
// Desktop: flex row centred within the 1200px box (all 3 cards fit, no nav).
// Tablet / Mobile: sliding transform track – offset applied inline by component.

export const cardsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'row', sm: 'column', md: 'row' },
  gap: `${24}px`, // fixed – must stay in sync with CARD_GAP_PX
  p: 0,
  m: 0,
  listStyle: 'none',
  userSelect: 'none',
  touchAction: 'pan-y',
  // Center on desktop where all cards fit; left-align on smaller screens.
  justifyContent: { xs: 'flex-start', lg: 'center' },
  alignItems: 'stretch',
  // transform + transition are applied inline in the component.
  willChange: 'transform'
};

// ─── Carousel clip wrapper ────────────────────────────────────────────────────
// overflow: visible so card box-shadows are never clipped.
// The scrollableContentSx ancestor already has overflowX: clip which clips
// cards that slide fully off-screen — no second hidden layer needed.

export const carouselClipSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
  overflow: 'visible'
};

// ─── Nav button wrapper (centred below carousel) ────────────────────────────

export const navGroupWrapperSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  mt: 2
};

// ─── Desktop nav button group ─────────────────────────────────────────────────

export const navGroupSx: SxProps<Theme> = {
  display: 'flex',
  gap: '2px',
  backgroundColor: '#FFFFFF',
  borderRadius: '999px',
  boxShadow: '0px 0px 15px 0px #00000026'
};

// ─── Single nav arrow button ──────────────────────────────────────────────────

export const navBtnSx: SxProps<Theme> = {
  backgroundColor: '#FFFFFF',
  color: '#005DA4',
  '&:hover': { backgroundColor: '#F0F7FF' },
  '&.Mui-disabled': { color: '#C9CACC', backgroundColor: '#FFFFFF' },
  '&:focus-visible': {
    outline: '3px solid #005DA4',
    outlineOffset: '2px'
  }
};

// ─── Card list item ────────────────────────────────────────────────────────────

export const cardListItemSx: SxProps<Theme> = {
  listStyle: 'none',
  p: 0,
  m: 0,
  display: 'flex'
};

// ─── Individual card ──────────────────────────────────────────────────────────

export const cardSx: SxProps<Theme> = {
  // Fixed 280 px width on every viewport (per spec)
  width: '280px',
  minWidth: { xs: '280px', sm: '100%', md: '280px' },
  minHeight: { xs: '288px', sm: '100%', md: '288px' },
  height: '100%',
  flexShrink: 0,
  borderRadius: '12px',
  backgroundColor: '#FFFFFF',
  boxShadow: '0px 0px 15px 0px #00000026',
  p: '24px',
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row', md: 'column' },
  alignItems: { xs: 'center', sm: 'flex-start' },
  gap: '16px',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  transition: 'box-shadow 0.2s ease, transform 0.15s ease',
  '&:hover': {
    boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.16)',
    transform: 'translateY(-2px)'
  },
  // WCAG 2.2 §2.4.11 – clear, high-contrast focus ring
  '&:focus-visible': {
    outline: '3px solid #005DA4',
    outlineOffset: '2px',
    boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.16)'
  }
};

// ─── Card icon image ──────────────────────────────────────────────────────────

export const cardIconSx: SxProps<Theme> = {
  width: { xs: '80px', sm: '80px' },
  height: { xs: '80px', sm: '80px' },
  objectFit: 'contain',
  flexShrink: 0
};

// ─── Card title ───────────────────────────────────────────────────────────────

export const cardTitleSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '16px', sm: '18px' },
  lineHeight: '22px',
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-primary, #000000)'
};

// ─── Card description ─────────────────────────────────────────────────────────

export const cardDescriptionSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0px',
  textAlign: { xs: 'center', sm: 'left' },
  color: 'var(--Sem-text-colour-text-primary, #000000)'
};

// ─── Card text container ───────────────────────────────────────────────────────

export const cardTextContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  justifyContent: {
    sm: 'center',
    xs: 'flex-start',
    md: 'flex-start'
  },
  height: { sm: '90px', xs: '100%', md: '100%' },
  textAlign: { xs: 'center', sm: 'left' }
};

// ─── Sticky footer ────────────────────────────────────────────────────────────

export const footerSx: SxProps<Theme> = {
  flexShrink: 0,
  backgroundColor: '#FFFFFF',
  px: { xs: 2, sm: 3, md: 4 },
  py: { xs: 2, md: 3 },
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0px 0px 15px 0px #00000026'
};
